import { Spinner } from '#lib/components/loading.jsx';
import { useState, useEffect, useRef } from 'react';
import { getSrcFromFile } from './helpers.js';
import { Icon } from '@iconify-icon/react';

export default function ImageManipulator({
    image,
    containerWidth,
    containerHeight,
    onTransformChange,
    initialState,
    rounded
}) {
    const imgRef = useRef(null);
    const [src, setSrc] = useState(image?.tempSrc ?? image?.src);

    useEffect(() => {
        const updateSrc = async () => {
            // Se non c'è un file, non fare nulla
            if (!image) return;
            // Se il image è un'immagine, ottieni il src e imposta l'immagine
            if (image instanceof Image || image?.tempSrc || image?.src) {
                setSrc(image?.tempSrc ?? image?.src);
            }
            if (image instanceof File) {
                // Se il image è un'immagine, convertilo in un'immagine e imposta l'immagine
                const src = await getSrcFromFile(image);
                setSrc(src);
            }
        };
        updateSrc();
    }, [image]);

    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [imageDimensions, setImageDimensions] = useState({
        width: 0,
        height: 0
    });
    const [transform, setTransform] = useState({
        x: 0,
        y: 0,
        zoom: 1,
        ...initialState
    });
    const [dragging, setDragging] = useState(false);
    const [lastMousePosition, setLastMousePosition] = useState({ x: 0, y: 0 });
    const containerRef = useRef();

    useEffect(() => {
        const updateSize = () => {
            if (imgRef.current && src && imgRef.current.width && imgRef.current.height) {
                const imgWidth = imgRef.current.width;
                const imgHeight = imgRef.current.height;
                setImageDimensions({ width: imgWidth, height: imgHeight });

                // Calcola il minZoom e le coordinate iniziali
                const minZoom = Math.max(containerWidth / imgWidth, containerHeight / imgHeight);
                const defaultPosition = {
                    x: (containerWidth - imgWidth * minZoom) / 2,
                    y: (containerHeight - imgHeight * minZoom) / 2
                };

                setTransform({
                    ...initialState,
                    x: defaultPosition.x,
                    y: defaultPosition.y,
                    zoom: minZoom
                });
                onTransformChange?.({
                    ...initialState,
                    x: defaultPosition.x,
                    y: defaultPosition.y,
                    zoom: minZoom
                });

                setIsLoading(false);
            }
        };

        const setError = () => {
            setIsLoading(false);
            setIsError(true);
        };

        // Aspetta il caricamento dell'immagine
        const img = imgRef.current;
        if (img.complete) {
            updateSize();
        } else {
            img.addEventListener('error', setError);
            img.addEventListener('load', updateSize);
        }

        // Cleanup listener per evitare memory leaks
        return () => {
            img.removeEventListener('load', updateSize);
            img.removeEventListener('error', setError);
            setIsLoading(true);
        };
    }, [src, imgRef.current]);

    // Utils
    const calculateBounds = (zoom) => {
        const imgWidth = imageDimensions.width * zoom;
        const imgHeight = imageDimensions.height * zoom;

        return {
            minX: Math.min(0, containerWidth - imgWidth),
            minY: Math.min(0, containerHeight - imgHeight),
            maxX: Math.max(0, containerWidth - imgWidth),
            maxY: Math.max(0, containerHeight - imgHeight)
        };
    };

    // Handler about positioning
    const handleMouseDown = (event) => {
        setDragging(true);
        setLastMousePosition({ x: event.clientX, y: event.clientY });
    };

    const handleMouseMove = (event) => {
        if (!dragging) return;

        const deltaX = event.clientX - lastMousePosition.x;
        const deltaY = event.clientY - lastMousePosition.y;

        setTransform((prev) => {
            const bounds = calculateBounds(prev.zoom);

            // Calcola la nuova posizione vincolata ai limiti
            const newPosition = {
                x: Math.min(bounds.maxX, Math.max(bounds.minX, prev.x + deltaX)),
                y: Math.min(bounds.maxY, Math.max(bounds.minY, prev.y + deltaY))
            };

            onTransformChange?.({ ...prev, x: newPosition.x, y: newPosition.y });
            return { ...prev, x: newPosition.x, y: newPosition.y };
        });

        setLastMousePosition({ x: event.clientX, y: event.clientY });
    };

    const handleMouseUp = () => {
        setDragging(false);
    };

    // Handler about zooming
    const handleWheel = (event) => {
        const deltaZoom = event.deltaY * -0.0005;
        const newZoom = Math.max(
            transform.zoom + deltaZoom,
            Math.min(containerWidth / imageDimensions.width, containerHeight / imageDimensions.height)
        );

        const zoomFactor = newZoom / transform.zoom;
        const centerX = containerWidth / 2;
        const centerY = containerHeight / 2;

        const newX = centerX - (centerX - transform.x) * zoomFactor;
        const newY = centerY - (centerY - transform.y) * zoomFactor;

        const bounds = calculateBounds(newZoom);

        // Vincola la nuova posizione ai limiti
        const constrainedPosition = {
            x: Math.min(bounds.maxX, Math.max(bounds.minX, newX)),
            y: Math.min(bounds.maxY, Math.max(bounds.minY, newY))
        };
        setTransform({
            ...transform,
            zoom: newZoom,
            x: constrainedPosition.x,
            y: constrainedPosition.y
        });
        onTransformChange?.({
            ...transform,
            zoom: newZoom,
            x: constrainedPosition.x,
            y: constrainedPosition.y
        });
    };

    // Mobile logic
    const handleTouchStart = (event) => {
        if (event.touches.length === 1) {
            const touch = event.touches[0];
            setDragging(true);
            setLastMousePosition({ x: touch.clientX, y: touch.clientY });
        }
    };

    const handleTouchMove = (event) => {
        if (!dragging || event.touches.length !== 1) return;

        const touch = event.touches[0];
        const deltaX = touch.clientX - lastMousePosition.x;
        const deltaY = touch.clientY - lastMousePosition.y;

        setTransform((prev) => {
            const bounds = calculateBounds(prev.zoom);

            const newPosition = {
                x: Math.min(bounds.maxX, Math.max(bounds.minX, prev.x + deltaX)),
                y: Math.min(bounds.maxY, Math.max(bounds.minY, prev.y + deltaY))
            };

            onTransformChange?.({ ...prev, x: newPosition.x, y: newPosition.y });
            return { ...prev, x: newPosition.x, y: newPosition.y };
        });

        setLastMousePosition({ x: touch.clientX, y: touch.clientY });
    };

    const handleTouchEnd = () => {
        setDragging(false);
    };
    return (
        <div className="relative w-full h-full">
            <div
                ref={containerRef}
                className={`w-full h-full relative overflow-hidden ${rounded ? 'rounded-full' : ''} bg-neutral-50`}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                <img
                    ref={imgRef}
                    src={src}
                    alt="image"
                    className={`absolute top-0 left-0 ${
                        dragging ? 'cursor-grabbing' : 'cursor-grab'
                    } transition-opacity ${isLoading ? 'opacity-0' : 'opacity-100'} -z-1`}
                    style={{
                        transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.zoom})`,
                        transformOrigin: 'top left'
                    }}
                    onMouseDown={handleMouseDown}
                    onWheel={handleWheel}
                    onTouchStart={handleTouchStart}
                    draggable={false}
                />

                {(isLoading || isError) && (
                    <div className="absolute top-0 left-0 flex items-center justify-center w-full h-full bg-neutral-50">
                        {isLoading && <Spinner />}
                        {isError && (
                            <p className="flex items-center justify-center w-full h-full p-4 text-lg font-bold text-neutral-600">
                                {_t.errors.generationImage()}
                            </p>
                        )}
                    </div>
                )}
            </div>
            {/* zoom handling */}
            <div className="absolute flex flex-col items-center justify-center gap-2 p-2 text-white border-2 rounded-full right-2 bottom-2 bg-neutral-900 border-neutral-50 bg-opacity-80">
                <Icon
                    icon="heroicons:magnifying-glass-plus-solid"
                    width={24}
                    height={24}
                    className="cursor-pointer hover:text-primary-500"
                    onClick={() => handleWheel({ deltaY: -100 })}
                />
                <Icon
                    icon="heroicons:magnifying-glass-minus-solid"
                    width={24}
                    height={24}
                    className="cursor-pointer hover:text-primary-500"
                    onClick={() => handleWheel({ deltaY: 100 })}
                />
            </div>
        </div>
    );
}
